import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { NgRedux } from '@redux/redux-compatibility.service';
import { actionsList, AppState } from '@shared/redux/store';
import { REEVAL_JOBS_STORAGE_KEY, ReevalJob, ReevalJobKind, ReevalJobTarget, reevalTargetKey } from '@shared/models/reeval-job.model';
import { getObjectPropValue } from '@shared/utils';
import { ProcessStatusPollingService } from './process-status-polling.service';
import { ReevalTileRefreshService } from './reeval-tile-refresh.service';

/** A run still not finished after this long is abandoned (also drops stale persisted jobs). */
const MAX_JOB_AGE_MS = 30 * 60 * 1000;

/**
 * Polls re-evaluation runs in the background. When a run reports changes while its document
 * is shown, the affected tile is marked "ready" and refreshed only when the user asks for it.
 * Jobs survive dialogs closing, leaving the document and (via sessionStorage) browser reloads;
 * ready marks live in memory and are dropped whenever the tiles reload anyway.
 */
@Injectable({ providedIn: 'root' })
export class ReevalJobService {

  private jobs = new Map<number, ReevalJob>();
  private pendingKeys$ = new BehaviorSubject<Set<string>>(new Set());
  private cancelAll$ = new Subject<void>();
  private readyTargets = new Map<string, ReevalJobTarget>();
  private readyKeys$ = new BehaviorSubject<Set<string>>(new Set());
  private docPageLoading = false;

  constructor(
    private ngRedux: NgRedux<AppState>,
    private processStatusPollingService: ProcessStatusPollingService,
    private tileRefreshService: ReevalTileRefreshService
  ) {
    this.ngRedux.subscribe(() => {
      const state = this.ngRedux.getState();
      if (state._currentAction === actionsList.USER_SIGN_OUT) {
        this.cancelAll();
      }
      // A page load that starts after a run finished fetches fresh tiles, so their ready marks are obsolete.
      if (state.docPageLoading && !this.docPageLoading) {
        this.clearReady();
      }
      this.docPageLoading = !!state.docPageLoading;
    });
  }

  /** Called by the rev-doc / cat-doc pages so finished jobs only touch a document that is actually shown. */
  setDocViewOpen(kind: ReevalJobKind, open: boolean): void {
    this.tileRefreshService.setDocViewOpen(kind, open);
    if (!open) {
      this.clearReady((target) => target.kind === kind);
    }
  }

  isAsyncEnabled(): boolean {
    return getObjectPropValue(this.ngRedux.getState().uiConfig, 'claim_tool.async_reeval_enabled') === true;
  }

  startJob(runNum: number, target: ReevalJobTarget): void {
    const job: ReevalJob = { runNum, target, createdAt: Date.now() };
    this.track(job);
    this.run(job);
  }

  /** Re-attaches to jobs persisted before a browser reload. Safe to call repeatedly. */
  resumePersistedJobs(): void {
    this.readPersisted()
      .filter((job) => !this.jobs.has(job.runNum))
      .forEach((job) => {
        this.track(job);
        this.run(job);
      });
  }

  pending$(target: ReevalJobTarget): Observable<boolean> {
    const key = reevalTargetKey(target);
    return this.pendingKeys$.pipe(map((keys) => keys.has(key)), distinctUntilChanged());
  }

  ready$(target: ReevalJobTarget): Observable<boolean> {
    const key = reevalTargetKey(target);
    return this.readyKeys$.pipe(map((keys) => keys.has(key)), distinctUntilChanged());
  }

  /**
   * Clears the element's ready mark and re-fetches it into its tiles and an open details dialog.
   * Never errors; completes once the data is merged (or the refresh was skipped).
   */
  refreshTile(target: ReevalJobTarget, preserveCurrentChanges = false): Observable<void> {
    this.clearReady((readyTarget) => reevalTargetKey(readyTarget) === reevalTargetKey(target));
    return this.tileRefreshService.refresh(target, preserveCurrentChanges).pipe(
      catchError((err) => {
        console.error('Could not refresh tile after background re-evaluation', err);
        return of(undefined);
      })
    );
  }

  /** Emits whenever the target's tile objects have been refreshed with fresh server data. */
  tileRefreshed$(target: ReevalJobTarget): Observable<void> {
    const key = reevalTargetKey(target);
    return this.tileRefreshService.refreshed$.pipe(
      filter((refreshed) => reevalTargetKey(refreshed) === key),
      map(() => undefined)
    );
  }

  /** Stops every job without persisting it (sign-out): the next user in this tab must not resume them. */
  private cancelAll(): void {
    this.jobs.clear();
    this.cancelAll$.next();
    this.persist();
    this.emitPendingKeys();
    this.clearReady();
  }

  private run(job: ReevalJob): void {
    const remainingMs = Math.max(0, job.createdAt + MAX_JOB_AGE_MS - Date.now());
    const expired$ = timer(remainingMs).pipe(
      tap(() => console.error(`Background re-evaluation run ${job.runNum} did not finish within ${MAX_JOB_AGE_MS / 60000} minutes; stopped polling`))
    );
    this.processStatusPollingService.pollUntilDone(job.runNum, { silent: true }).pipe(
      tap(() => {
        if (this.tileRefreshService.isDocOpen(job.target)) {
          this.markReady(job.target);
        }
      }),
      catchError((err) => {
        console.error(`Background re-evaluation run ${job.runNum} failed`, err);
        return of(undefined);
      }),
      takeUntil(merge(this.cancelAll$, expired$)),
      finalize(() => this.untrack(job.runNum))
    ).subscribe();
  }

  private markReady(target: ReevalJobTarget): void {
    this.readyTargets.set(reevalTargetKey(target), target);
    this.readyKeys$.next(new Set(this.readyTargets.keys()));
  }

  private clearReady(predicate: (target: ReevalJobTarget) => boolean = () => true): void {
    const keys = [...this.readyTargets.entries()].filter(([, target]) => predicate(target)).map(([key]) => key);
    if (keys.length === 0) {
      return;
    }
    keys.forEach((key) => this.readyTargets.delete(key));
    this.readyKeys$.next(new Set(this.readyTargets.keys()));
  }

  private track(job: ReevalJob): void {
    this.jobs.set(job.runNum, job);
    this.persist();
    this.emitPendingKeys();
  }

  private untrack(runNum: number): void {
    this.jobs.delete(runNum);
    this.persist();
    this.emitPendingKeys();
  }

  private emitPendingKeys(): void {
    this.pendingKeys$.next(new Set([...this.jobs.values()].map((job) => reevalTargetKey(job.target))));
  }

  private persist(): void {
    try {
      const jobs = [...this.jobs.values()];
      if (jobs.length > 0) {
        sessionStorage.setItem(REEVAL_JOBS_STORAGE_KEY, JSON.stringify(jobs));
      } else {
        sessionStorage.removeItem(REEVAL_JOBS_STORAGE_KEY);
      }
    } catch (err) {
      console.error('Could not persist background re-evaluation jobs', err);
    }
  }

  private readPersisted(): ReevalJob[] {
    try {
      const raw = sessionStorage.getItem(REEVAL_JOBS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((job) =>
            typeof job?.runNum === 'number' &&
            job?.target &&
            typeof job?.createdAt === 'number' &&
            Date.now() - job.createdAt < MAX_JOB_AGE_MS)
        : [];
    } catch (err) {
      console.error('Could not read persisted background re-evaluation jobs', err);
      return [];
    }
  }
}
